/**
 * MapperImportDrawer — slide-in import launcher opened from the header.
 *
 * Thin wrapper over the existing import pipeline: it picks a file and dispatches
 * `mapper:import-file`, which FileImportOverlay consumes to parse → preview →
 * commit (reusing the existing backend /mapper/import + /import/commit). The
 * drawer also surfaces the supported formats and the standardization note that
 * the backend attaches to the preview (contextualizer-registry IKU mapping).
 */

import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, X, FileText, Check, AlertCircle } from 'lucide-react';
import { useMapStore } from '../MapContext';
import { MAPPER_OPEN_IMPORT_EVENT } from '../constants';

const SUPPORTED = 'Shapefile (.zip), GeoJSON, KML/KMZ, GPX, CSV (with lat/lng). GeoPackage (.gpkg) coming soon.';

export default function MapperImportDrawer() {
  const [open, setOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { importPreview, importStep } = useMapStore();
  const standardization = (importPreview as { standardization?: { mappings?: Array<{ source_type: string; matched: boolean; iku_type?: string | null }>; resolved?: number; note?: string } } | null)?.standardization;

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener(MAPPER_OPEN_IMPORT_EVENT, onOpen);
    return () => window.removeEventListener(MAPPER_OPEN_IMPORT_EVENT, onOpen);
  }, []);

  const pickFile = (file: File) => {
    window.dispatchEvent(new CustomEvent('mapper:import-file', { detail: file }));
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-40 bg-black/20"
            onClick={() => setOpen(false)}
          />
          <motion.div
            initial={{ x: 360 }}
            animate={{ x: 0 }}
            exit={{ x: 360 }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            className="absolute right-0 top-0 z-40 flex h-full w-[360px] flex-col border-l border-gray-200 bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <div className="flex items-center gap-2">
                <Upload className="h-4 w-4 text-purple-600" />
                <span className="text-sm font-semibold text-gray-800">Import map data</span>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto p-4">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-purple-200 bg-purple-50/50 px-4 py-8 text-center transition-colors hover:border-purple-300 hover:bg-purple-50"
              >
                <FileText className="h-8 w-8 text-purple-400" />
                <span className="text-sm font-medium text-gray-700">Choose a file to import</span>
                <span className="text-[11px] text-gray-400">or drag &amp; drop onto the map</span>
              </button>

              <p className="text-[11px] leading-relaxed text-gray-500">
                <span className="font-medium text-gray-600">Supported:</span> {SUPPORTED}
              </p>

              {importStep !== 'idle' && (
                <div className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
                  {importStep === 'preview'
                    ? `Preview ready — confirm in the bar at the bottom of the map.`
                    : `Processing (${importStep})…`}
                </div>
              )}

              {standardization && standardization.mappings && standardization.mappings.length > 0 && (
                <div className="rounded-lg border border-gray-200 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-700">Standardization</span>
                    <span className="text-[10px] text-gray-400">
                      {standardization.resolved}/{standardization.mappings.length} typed
                    </span>
                  </div>
                  <ul className="space-y-1">
                    {standardization.mappings.map((m) => (
                      <li key={m.source_type} className="flex items-center justify-between gap-2 text-[11px]">
                        <span className="truncate text-gray-600">{m.source_type}</span>
                        {m.matched ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600">
                            <Check className="h-3 w-3" />
                            <span className="truncate max-w-[140px]">{m.iku_type}</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-600">
                            <AlertCircle className="h-3 w-3" />
                            unmatched
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                  {standardization.note && (
                    <p className="mt-2 text-[10px] text-gray-400">{standardization.note}</p>
                  )}
                </div>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept=".geojson,.json,.kml,.kmz,.zip,.shp,.gpx,.csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) pickFile(file);
                e.target.value = '';
              }}
            />
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
